import {
  EnvironmentDto,
  EnvironmentGetResponse,
  FeatureFlagEnvironmentDetailDto,
  FeatureFlagEnvironmentStateGetResponse,
  OrganizationDto,
  OrganizationGetResponse,
  UserDto,
  UserGetResponse,
  UserOrganizationDto,
} from '@flagsync/skyline';
import {
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAppContext } from '@/components/dashboard-shell/hooks/app-context-provider';

import { pricing } from '@/pages/unauthenticated/pricing/components/constants';
import { sky } from '@/services/api-client';
import { AppContext, AppContextIdMap } from '@/services/types';
import { ServiceError, ServiceErrorCode } from '@/services/utils/service-error';
import { useNavigate } from 'react-router';
import { cookies } from '@/utils/horizon-cookies.client';

export const APP_CONTEXT_QUERY_KEY = ['app-context'] as const;

const QUERY_KEY = {
  EnvironmentContext: 'environment-context',
  OrganizationContext: 'organization-context',
  FeatureFlagEnvironmentContext: 'feature-flag-environment-context',
} as const;

// Prefix covering every cached environment context, for mutations on
// workspace-level entities (e.g. flags) that exist in all environments.
export const ENVIRONMENT_CONTEXT_BASE_KEY = [
  QUERY_KEY.EnvironmentContext,
] as const;

export const buildEnvironmentContextKey = (environmentId: number) =>
  [QUERY_KEY.EnvironmentContext, environmentId] as const;

export const buildOrganizationKey = (organizationId: number) =>
  [QUERY_KEY.OrganizationContext, organizationId] as const;

export const buildFeatureFlagEnvironmentDetailKey = (
  environmentId: number,
  publicFeatureFlagId: string,
) =>
  [
    QUERY_KEY.FeatureFlagEnvironmentContext,
    environmentId,
    publicFeatureFlagId,
  ] as const;

export const useGetEnvironment = (
  options?: Omit<
    UseQueryOptions<EnvironmentDto, ServiceError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const {
    appContext: { environment },
  } = useAppContext();

  return useQuery<EnvironmentDto, ServiceError>({
    queryKey: buildEnvironmentContextKey(environment.environmentId),
    queryFn: async () => {
      const response = await sky.get<EnvironmentGetResponse>(
        `/environment/${environment.environmentId}`,
      );
      const env = response?.environment;
      if (!env) throw new Error('Environment not found');
      return env;
    },
    ...options,
  });
};

export const useOrganization = (
  options?: Omit<
    UseQueryOptions<OrganizationDto, ServiceError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const {
    appContext: { organization },
  } = useAppContext();

  return useQuery<OrganizationDto, ServiceError>({
    queryKey: buildOrganizationKey(organization.organizationId),
    queryFn: async () => {
      const response = await sky.get<OrganizationGetResponse>(
        `/organization/${organization.organizationId}`,
      );
      const org = response?.organization;
      if (!org) throw new Error('Organization not found');
      return org;
    },
    ...options,
  });
};

/**
 * Fetch a flag's environment detail by explicit ids rather than the current app
 * context. Shares the cache key with {@link useFeatureFlagEnvironmentDetail}, so
 * a flag fetched here (e.g. per audit event) is reused by the flag detail page
 * and vice versa. Useful when rendering data that spans environments/workspaces.
 */
export const useFeatureFlagEnvironmentDetailExplicit = (
  {
    publicFeatureFlagId,
    workspaceId,
    environmentId,
  }: {
    publicFeatureFlagId: string;
    workspaceId: number;
    environmentId: number;
  },
  options?: Omit<
    UseQueryOptions<FeatureFlagEnvironmentDetailDto, ServiceError>,
    'queryKey' | 'queryFn'
  >,
) => {
  // workspaceId is deliberately omitted from the key: an environmentId
  // belongs to exactly one workspace, so the key is already unique.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery<FeatureFlagEnvironmentDetailDto, ServiceError>({
    queryKey: buildFeatureFlagEnvironmentDetailKey(
      environmentId,
      publicFeatureFlagId,
    ),
    queryFn: async () => {
      const response = await sky.get<FeatureFlagEnvironmentStateGetResponse>(
        `/flag/${publicFeatureFlagId}/workspaceId/${workspaceId}/environment/${environmentId}`,
      );
      const ffes = response?.detail;
      if (!ffes) throw new Error('Feature flag environment not found');
      return ffes;
    },
    ...options,
  });
};

export const useFeatureFlagEnvironmentDetail = (
  publicFeatureFlagId: string,
  options?: Omit<
    UseQueryOptions<FeatureFlagEnvironmentDetailDto, ServiceError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const {
    appContext: { environment, workspace },
  } = useAppContext();

  return useFeatureFlagEnvironmentDetailExplicit(
    {
      publicFeatureFlagId,
      workspaceId: workspace.workspaceId,
      environmentId: environment.environmentId,
    },
    options,
  );
};

export const useGetAppContext = (
  options?: Omit<
    UseQueryOptions<AppContext, ServiceError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<AppContext, ServiceError>({
    queryKey: APP_CONTEXT_QUERY_KEY,
    queryFn: async () => {
      const response = await sky.get<UserGetResponse>('/user/me');
      const user = response?.user;
      if (!user) {
        throw new ServiceError({
          errorCode: ServiceErrorCode.UnknownAccount,
          message: 'No authenticated user',
        });
      }
      return resolveAppContext(user, response.viewAs ?? false);
    },
    staleTime: Infinity,
    retry: false,
    ...options,
  });
};

export const useInvalidateAppContext = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: APP_CONTEXT_QUERY_KEY });
};

export const useRedirectIfAuthenticated = (
  redirectTo: string = '/dashboard',
) => {
  const navigate = useNavigate();
  const query = useGetAppContext({ retry: false });

  useEffect(() => {
    if (query.data) {
      navigate(redirectTo, { replace: true });
    }
  }, [query.data, redirectTo, navigate]);

  return query;
};

export const useRedirectIfUnauthenticated = (redirectTo: string = '/login') => {
  const navigate = useNavigate();
  const query = useGetAppContext({ retry: false });

  useEffect(() => {
    if (query.isError) {
      navigate(redirectTo, { replace: true });
    }
  }, [query.isError, redirectTo, navigate]);

  return query;
};

export const setAppContextCookie = (data: AppContextIdMap) => {
  cookies().set('ORG_CTX', btoa(JSON.stringify(data)), {
    expires: 365,
    path: '/',
    sameSite: 'lax',
  });
};

export const deleteAppContextCookie = () => {
  cookies().delete('ORG_CTX', { path: '/' });
};

function resolveAppContext(user: UserDto, viewAs: boolean): AppContext {
  const ctx = readAppContextIdMap(user);

  let organization = user.organizations.find(
    (o) => o.organizationId === ctx.organizationId,
  );
  let workspace = organization?.workspaces.find(
    (w) => w.workspaceId === ctx.workspaceId,
  );
  let environment = workspace?.environments.find(
    (e) => e.environmentId === ctx.environmentId,
  );

  // The stored ORG_CTX cookie points at a context this user doesn't have —
  // e.g. right after a God View-as cookie swap (the cookie still holds the
  // God's org), or after being removed from an org. Fall back to the user's
  // first org rather than throwing, which would (mis)classify an authenticated
  // user as logged-out and bounce them to /login → /dashboard in a loop.
  if (!organization || !workspace || !environment) {
    organization = user.organizations[0];
    workspace = organization?.workspaces[0];
    environment = workspace?.environments[0];
  }

  if (!organization || !workspace || !environment) {
    throw new ServiceError({
      errorCode: ServiceErrorCode.UserOrganizationCtxNotFound,
      message:
        'Failed to find organization, workspace, or environment from context',
    });
  }

  const planTier = pricing.tiers.find((t) => {
    if (!organization.stripe?.planTier) return false;
    return t.lookupKeys.includes(organization.stripe.planTier);
  })?.activeTier;

  return {
    user,
    organization,
    workspace,
    environment,
    planTier,
    viewAs,
  };
}

function getInitialContext(organization: UserOrganizationDto): AppContextIdMap {
  return {
    organizationId: organization.organizationId,
    workspaceId: organization.workspaces[0].workspaceId,
    environmentId: organization.workspaces[0].environments[0].environmentId,
  };
}

function readAppContextIdMap(user: UserDto): AppContextIdMap {
  const defaultContext = getInitialContext(user.organizations[0]);

  const cookie = cookies().get('ORG_CTX');
  if (!cookie) return defaultContext;

  try {
    return JSON.parse(atob(cookie.value)) as AppContextIdMap;
  } catch {
    return defaultContext;
  }
}
